import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CompositionPlayer from '../components/CompositionPlayer';
import BakeReceipt from '../components/BakeReceipt';
import { useMovie } from '../data/firestore';
import { useAuth } from '../AuthContext';
import { httpsify } from '../utils/url';
import { movieClips } from '../utils/movie';
import { renderEtaLabel, useNow } from '../utils/eta';

export default function Watch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { doc: movie } = useMovie(id);
  const rStatus = movie?.render?.status;
  const now = useNow(rStatus === 'rendering' || rStatus === 'queued');
  // Viewer's soundtrack choice: undefined = auto (first option), 'orig' = the
  // movie's own audio, otherwise a viewer-song id.
  const [songChoice, setSongChoice] = useState(undefined);

  if (movie === undefined) return <div className="cs-page">Loading…</div>;
  if (movie === null) return <div className="cs-page">Movie not found.</div>;

  // A published render is a finished MP4/HLS with the OWNED audio (original +
  // uploaded songs) baked in. Cloud tracks (YouTube/Spotify/SoundCloud) are
  // licensed-stream-only and never baked into the file — but we still overlay
  // them in-app, synced to the timeline, so playback includes that music.
  const published = movie.render?.status === 'ready' && movie.streamUrl;
  const clips = published
    ? [{ id: 'published', label: movie.title, url: movie.streamUrl, streamType: movie.streamType || 'hls', inSec: 0, outSec: movie.durationSec || 0, sourceDurationSec: movie.durationSec || 0 }]
    : movieClips(movie);

  // Full-quality download: the publish uploads the graded full-res master and
  // records it at movie.download. Uploaded songs are BAKED into that file, so
  // those movies download with their soundtrack; streaming music (YouTube etc.)
  // is licensed-stream-only and never in the file — the download is offered
  // without it.
  const download = movie.download?.status === 'ready' && movie.download.url ? movie.download : null;
  // Above 4K, H.264 exceeds every hardware decoder (max 4096×2304) and
  // software-decodes as a slideshow — HEVC hardware-decodes up to 8K. Newer
  // publishes bake the master as HEVC directly (download.codec); older ones may
  // carry a transcoded companion at download.hevc. Prefer the playable file,
  // keep the H.264 one as a max-compatibility/archival secondary.
  const hevcDl = download?.codec === 'hevc'
    ? download
    : (download?.hevc?.status === 'ready' && download.hevc.url ? download.hevc : null);
  const h264Dl = download && download.codec !== 'hevc' ? download : null;
  const primaryDl = hevcDl || h264Dl;
  const archivalDl = hevcDl && h264Dl ? h264Dl : null;
  // Full-length share renditions at common heights (2880/2160/1440/1080p),
  // lanczos-downscaled server-side at publish — the quality picker between the
  // huge master and the split set.
  const renditions = movie.downloadRenditions?.status === 'ready' ? movie.downloadRenditions.items || [] : [];
  // Social split set: the publish also cuts the movie into equal ≤60s H.264
  // parts (Instagram carousel slides cap at 60s) — same grade + soundtrack.
  const splitParts = movie.downloadParts?.status === 'ready' && movie.downloadParts.items?.length
    ? movie.downloadParts
    : null;
  const hasUploadedSongs = (movie.songs || []).some((s) => s.source === 'upload' && s.url);
  const hasStreamingSongs =
    (movie.songs || []).some((s) => s.source !== 'upload') || (movie.viewerSongs || []).length > 0;

  // Viewer-selectable soundtracks: when present, the watcher picks one and it
  // overlays live (synced to the movie), muting the movie's own audio. This is
  // independent of the baked `songs` timeline used for the owner's edit.
  const viewerSongs = movie.viewerSongs || [];
  const hasViewerSongs = viewerSongs.length > 0;
  // Owner-chosen default plays first; fall back to the first song. A saved
  // default that no longer exists falls back too.
  const savedDefault =
    movie.viewerSongDefault === 'orig'
      ? 'orig'
      : viewerSongs.some((s) => s.id === movie.viewerSongDefault)
        ? movie.viewerSongDefault
        : viewerSongs[0]?.id || 'orig';
  const choice = songChoice !== undefined ? songChoice : savedDefault;
  const chosen = hasViewerSongs && choice !== 'orig'
    ? viewerSongs.find((s) => s.id === choice)
    : null;

  let songs;
  let hasOriginalAudio;
  if (hasViewerSongs) {
    const movieDur = movie.durationSec || 0;
    songs = chosen
      ? [{
          ...chosen,
          movieOffset: 0,
          trimStart: 0,
          trimEnd: chosen.songDurationSec || movieDur || 0,
          volume: 1,
          fadeInSec: 0,
          fadeOutSec: 1,
        }]
      : [];
    hasOriginalAudio = chosen ? false : movie.hasOriginalAudio !== false;
  } else if (published) {
    // Uploaded songs are baked into the published file — but ONLY the ones
    // present at bake time (render.bakedSongIds). A song uploaded AFTER the
    // bake isn't in the file yet, so it overlays live until the next publish.
    // Old publishes without the stamp fall back to "all uploads are baked".
    const baked = movie.render?.bakedSongIds;
    songs = (movie.songs || []).filter((s) =>
      s.source === 'upload' ? (baked ? !baked.includes(s.id) : false) : true
    );
    hasOriginalAudio = movie.hasOriginalAudio !== false;
  } else {
    songs = movie.songs || [];
    hasOriginalAudio = movie.hasOriginalAudio !== false;
  }

  return (
    <div className="cs-player-wrap">
      {movie.status === 'uploading' ? (
        <div className="cs-page">
          <div className="cs-empty">
            <strong>{movie.title}</strong> is still uploading — check back in a moment.
          </div>
        </div>
      ) : clips.length ? (
        <CompositionPlayer
          clips={clips}
          songs={songs}
          hasOriginalAudio={hasOriginalAudio}
          intro={published ? 0 : movie.intro || 0}
          outro={published ? 0 : movie.outro || 0}
          poster={httpsify(movie.heroUrl)}
        />
      ) : (
        <div className="cs-page">
          <div className="cs-empty">
            <div style={{ marginBottom: 16 }}>
              No video set for <strong>{movie.title}</strong> yet.
            </div>
            {user && (
              <button
                className="cs-btn cs-btn-primary"
                onClick={() => navigate(`/movies/${id}/edit`)}
              >
                Set up this movie
              </button>
            )}
          </div>
        </div>
      )}
      <div className="cs-player-info">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => navigate(-1)}>
            ← Back
          </button>
          {user && (
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={() => navigate(`/movies/${id}/edit`)}
            >
              ✎ Edit movie
            </button>
          )}
          {primaryDl && (
            <details className="cs-dl-menu">
              <summary
                className="cs-btn cs-btn-ghost cs-btn-sm"
                title={
                  'Pick a quality — all files carry the full grade' +
                  (hasUploadedSongs
                    ? ' and soundtrack'
                    : hasStreamingSongs
                      ? '; the streamed soundtrack is licensed for streaming only and not in the files'
                      : '')
                }
              >
                ⬇ Download{hasStreamingSongs && !hasUploadedSongs ? ' (no song)' : ''} ▾
              </summary>
              <div
                className="cs-dl-menu-list"
                onClick={(e) => e.currentTarget.closest('details')?.removeAttribute('open')}
              >
                <a
                  href={httpsify(primaryDl.url)}
                  download
                  title={primaryDl === hevcDl ? 'HEVC — hardware-decodes smoothly on modern devices' : ''}
                >
                  <span>Max quality{primaryDl.height || download.height ? ` · ${primaryDl.height || download.height}p` : ''}{primaryDl === hevcDl ? ' HEVC' : ''}</span>
                  {primaryDl.sizeBytes ? <span className="cs-dl-meta">{(primaryDl.sizeBytes / 1024 ** 3).toFixed(1)} GB</span> : null}
                </a>
                {renditions.map((r) => (
                  <a
                    key={r.key || r.url}
                    href={httpsify(r.url)}
                    download
                    title={`${r.height}p ${r.codec === 'hevc' ? 'HEVC' : 'H.264'} — downscaled cleanly from the graded master`}
                  >
                    <span>{r.height}p{r.codec === 'hevc' ? ' HEVC' : ''}</span>
                    {r.sizeBytes ? (
                      <span className="cs-dl-meta">
                        {r.sizeBytes >= 1024 ** 3 ? `${(r.sizeBytes / 1024 ** 3).toFixed(1)} GB` : `${Math.round(r.sizeBytes / 1024 ** 2)} MB`}
                      </span>
                    ) : null}
                  </a>
                ))}
                {archivalDl && (
                  <a
                    href={httpsify(archivalDl.url)}
                    download
                    title="H.264 archival master — maximum tool compatibility, but above 4K it software-decodes (choppy playback on laptops)"
                  >
                    <span>H.264 archival{archivalDl.height ? ` · ${archivalDl.height}p` : ''}</span>
                    {archivalDl.sizeBytes ? <span className="cs-dl-meta">{(archivalDl.sizeBytes / 1024 ** 3).toFixed(1)} GB</span> : null}
                  </a>
                )}
              </div>
            </details>
          )}
        </div>
        {splitParts && (
          <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span
              className="cs-help"
              title={`Equal parts of at most ${splitParts.partSec}s — Instagram carousel slides cap at 60s per video. ${splitParts.height}p H.264 with the soundtrack, downscaled cleanly from the graded master.`}
            >
              Social split set ({splitParts.count} × ≤{splitParts.partSec}s · {splitParts.height}p)
            </span>
            {splitParts.items.map((p) => (
              <a
                key={p.index}
                className="cs-btn cs-btn-ghost cs-btn-sm"
                href={httpsify(p.url)}
                download
                title={`Part ${p.index} · ${Math.round(p.durSec)}s${p.sizeBytes ? ` · ${(p.sizeBytes / 1024 ** 2).toFixed(0)} MB` : ''}`}
              >
                ⬇ Part {p.index}
              </a>
            ))}
          </div>
        )}
        {/* Bake receipt: per-stage time + cost metadata stamped by the publish.
            Signed-in = allowlisted (auth is quarantined per site), so `user`
            is the same authorization gate as the Edit button above. */}
        {user && movie.render?.receipt && (
          <BakeReceipt receipt={movie.render.receipt} style={{ marginTop: 12 }} />
        )}
        {(rStatus === 'rendering' || rStatus === 'queued') && (
          <div className="cs-help" style={{ marginTop: 12, color: 'var(--surf)' }}>
            ● {renderEtaLabel(movie.render, now)} — you're watching the live preview; the polished
            version replaces it automatically when ready.
          </div>
        )}
        {hasViewerSongs && (
          <div style={{ marginTop: 16 }}>
            <div className="cs-help" style={{ marginBottom: 6 }}>Soundtrack</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {viewerSongs.map((s) => (
                <button
                  key={s.id}
                  className={`cs-btn cs-btn-sm ${choice === s.id ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                  onClick={() => setSongChoice(s.id)}
                >
                  ♪ {s.label}
                </button>
              ))}
              <button
                className={`cs-btn cs-btn-sm ${choice === 'orig' ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                onClick={() => setSongChoice('orig')}
              >
                Original audio
              </button>
            </div>
          </div>
        )}
        <h1 style={{ marginTop: 16 }}>{movie.title}</h1>
        <div className="cs-hero-meta">
          {[movie.location, movie.year].filter(Boolean).join(' · ')}
        </div>
        {movie.synopsis && <p style={{ maxWidth: 720, color: '#ddd' }}>{movie.synopsis}</p>}
      </div>
    </div>
  );
}
